import Enums from "@/enums";

const TAKER_TEST_CODE_KEY = 'code';
const TAKER_TEST_CTT_ID_KEY = 'cttId';
const TAKER_ENTRY_CODE_KEY = 'entryCode';
const TAKER_SESSION_ACTIVE_KEY = 'takerSessionActive';

function normalizeTakerCode(code: string): string {
    return String(code || '').trim().toUpperCase();
}

export function getStoredTakerTestCode(): string {
    return String(localStorage.getItem(TAKER_TEST_CODE_KEY) || '').trim();
}

export function getStoredTakerEntryCode(): string {
    return String(localStorage.getItem(TAKER_ENTRY_CODE_KEY) || '').trim();
}

export function getStoredTakerCttId(): number {
    const parsed = Number(localStorage.getItem(TAKER_TEST_CTT_ID_KEY) || 0);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function persistTakerTestIdentity(
    code: string,
    cttId?: number | null,
    entryCode?: string | null
): void {
    const trimmedCode = String(code || '').trim();
    if (trimmedCode) {
        localStorage.setItem(TAKER_TEST_CODE_KEY, trimmedCode);
    }

    const resolvedCttId = Number(cttId || 0);
    if (Number.isFinite(resolvedCttId) && resolvedCttId > 0) {
        localStorage.setItem(TAKER_TEST_CTT_ID_KEY, String(resolvedCttId));
    }

    if (entryCode !== undefined) {
        const trimmedEntryCode = String(entryCode || '').trim();
        if (trimmedEntryCode) {
            localStorage.setItem(TAKER_ENTRY_CODE_KEY, trimmedEntryCode);
        } else {
            localStorage.removeItem(TAKER_ENTRY_CODE_KEY);
        }
    }
}

export function clearTakerTestIdentity(): void {
    localStorage.removeItem(TAKER_TEST_CODE_KEY);
    localStorage.removeItem(TAKER_TEST_CTT_ID_KEY);
    localStorage.removeItem(TAKER_ENTRY_CODE_KEY);
    clearTakerSessionActive();
}

export function markTakerSessionActive(): void {
    sessionStorage.setItem(TAKER_SESSION_ACTIVE_KEY, '1');
}

export function hasActiveTakerSession(): boolean {
    return sessionStorage.getItem(TAKER_SESSION_ACTIVE_KEY) === '1';
}

export function clearTakerSessionActive(): void {
    sessionStorage.removeItem(TAKER_SESSION_ACTIVE_KEY);
}

/** Full page reload keeps sessionStorage; clear the in-tab flag so StartTest can show restart overlays. */
export function clearTakerSessionActiveOnReload(): void {
    if (typeof window === 'undefined' || typeof performance === 'undefined') return;

    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (nav?.type === 'reload') {
        clearTakerSessionActive();
    }
}

/** Drop admin/session keys without treating this tab as a later revisit of the test. */
export function clearSessionStoragePreservingTakerSession(): void {
    const active = hasActiveTakerSession();
    sessionStorage.clear();
    if (active) markTakerSessionActive();
}

export function isCttEntryCode(code: string): boolean {
    return /^CTT/i.test(String(code || '').trim());
}

export function isTestEntryCode(code: string): boolean {
    const normalized = normalizeTakerCode(code);
    return /^T/.test(normalized) && !/^CTT/.test(normalized);
}

/** True when the requested per-test code belongs to the stored taker session. */
export function codesRepresentSameTakerTest(
    requestedCode: string,
    storedEntryCode: string,
    storedTestCode: string
): boolean {
    const requested = normalizeTakerCode(requestedCode);
    if (!requested) return false;

    const entry = normalizeTakerCode(storedEntryCode);
    const test = normalizeTakerCode(storedTestCode);

    if (entry && requested === entry) return true;
    if (test && requested === test) return true;

    return false;
}

/** Clear stored restart data when opening a different per-test code than the saved session. */
export function shouldClearStoredTakerDataForTestCode(
    requestedTestCode: string,
    storedEntryCode: string,
    storedTestCode: string
): boolean {
    if (!isTestEntryCode(requestedTestCode)) return false;

    const entry = normalizeTakerCode(storedEntryCode);
    const test = normalizeTakerCode(storedTestCode);
    if (!entry && !test) return false;

    return !codesRepresentSameTakerTest(requestedTestCode, storedEntryCode, storedTestCode);
}

/** Clear stored restart data when opening a different CTT code than the saved session. */
export function shouldClearStoredTakerDataForCttEntry(
    requestedCttCode: string,
    storedCttCode: string
): boolean {
    const requested = normalizeTakerCode(requestedCttCode);
    const stored = normalizeTakerCode(storedCttCode);
    if (!requested || !isCttEntryCode(requested)) return false;
    if (!stored) return false;
    return requested !== stored;
}

export type CttEntryResumeAction = 'attempt-resume' | 'start-fresh' | 'clear-mismatch' | 'noop';

/**
 * Decide how to handle a CTT-code entry when prior taker session data exists.
 * - attempt-resume: CTT is restartable and the stored session belongs to this CTT; try to resume it.
 * - start-fresh: CTT is not restartable but the stored session belongs to this CTT; clear state and start a new test.
 * - clear-mismatch: the stored session belongs to a different CTT; clear the stale stored data.
 * - noop: not a CTT code, or there is no stored session to act on.
 */
export function resolveCttEntryResumeAction(params: {
    enteredCode: string;
    storedTestCode: string;
    storedCttId: number;
    storedCttCode: string;
    cttRestartable: boolean;
}): CttEntryResumeAction {
    const { enteredCode, storedTestCode, storedCttId, storedCttCode, cttRestartable } = params;

    if (!isCttEntryCode(enteredCode)) return 'noop';
    if (!String(storedTestCode || '').trim() || Number(storedCttId || 0) <= 0) return 'noop';

    const matchesEnteredCtt =
        !!storedCttCode && normalizeTakerCode(storedCttCode) === normalizeTakerCode(enteredCode);

    if (matchesEnteredCtt) {
        return cttRestartable ? 'attempt-resume' : 'start-fresh';
    }

    if (shouldClearStoredTakerDataForCttEntry(enteredCode, storedCttCode)) {
        return 'clear-mismatch';
    }

    return 'noop';
}

export function shouldSendStoredCodeForCtt(
    storedCode: string,
    storedCttId: number,
    requestedCttId: number
): boolean {
    const code = String(storedCode || '').trim();
    const cttId = Number(requestedCttId || 0);
    const persistedCttId = Number(storedCttId || 0);
    if (!code || !/^T/i.test(code)) return false;
    if (!cttId || !persistedCttId) return false;
    return persistedCttId === cttId;
}

function readBlockingStartTestMessage(payload: Record<string, unknown> | null | undefined): string {
    const messages = Array.isArray(payload?.messages) ? payload.messages : [];
    const fromMessages = messages
        .map((message) => {
            if (typeof message === 'string') return message;
            if (message && typeof message === 'object') {
                const record = message as Record<string, unknown>;
                return String(record.message ?? record.text ?? '');
            }
            return '';
        })
        .find((text) => text.trim().length > 0);

    if (fromMessages?.trim()) return fromMessages.trim();

    for (const key of ['message', 'errorMessage', 'error'] as const) {
        const value = payload?.[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
    }

    return '';
}

function isStartTestInProgressForCtt(
    payload: Record<string, unknown> | null | undefined,
    expectedCttId: number
): boolean {
    const resolvedCttId = Number(expectedCttId || 0);
    if (!resolvedCttId) return false;
    if (readBlockingStartTestMessage(payload)) return false;

    const testData =
        payload?.testData && typeof payload.testData === 'object'
            ? (payload.testData as Record<string, unknown>)
            : null;
    if (!testData) return false;

    const storedCttId = Number(testData.companyTestTypeId || 0);
    if (storedCttId !== resolvedCttId) return false;

    const stateId = Number(testData.stateId || 0);
    if (stateId >= 301) return false;

    const code = String(testData.code || payload?.code || '').trim();
    return /^T/i.test(code);
}

/** True when StartTest payload represents an in-progress test for the requested CTT. */
export function isStartTestResumableForCtt(
    payload: Record<string, unknown> | null | undefined,
    expectedCttId: number
): boolean {
    if (!readCttRestartableFromStartTest(payload, expectedCttId)) return false;
    return isStartTestInProgressForCtt(payload, expectedCttId);
}

/** Read the CTT-level restartable flag from a StartTest payload (never the TT template default). */
export function readCttRestartableFromStartTest(
    payload: Record<string, unknown> | null | undefined,
    expectedCttId?: number
): boolean {
    const testTypes = Array.isArray(payload?.testTypes) ? payload.testTypes : [];
    const resolvedCttId = Number(
        expectedCttId || (payload?.testData as Record<string, unknown> | undefined)?.companyTestTypeId || 0
    );
    const match = resolvedCttId > 0
        ? testTypes.find((tt) => {
            if (!tt || typeof tt !== 'object') return false;
            const record = tt as Record<string, unknown>;
            const companyTestType =
                record.companyTestType && typeof record.companyTestType === 'object'
                    ? (record.companyTestType as Record<string, unknown>)
                    : null;
            return Number(companyTestType?.id || 0) === resolvedCttId;
        })
        : undefined;
    const selected = match ?? (testTypes[0] && typeof testTypes[0] === 'object' ? testTypes[0] as Record<string, unknown> : null);
    const companyTestType =
        selected?.companyTestType && typeof selected.companyTestType === 'object'
            ? (selected.companyTestType as Record<string, unknown>)
            : null;
    return Enums.resumesExistingAttempt(companyTestType?.restartable);
}

/**
 * When a non-restartable CTT test is re-opened after the browser session ended,
 * start a brand-new attempt instead of resuming mid-test progress.
 */
export function resolveNonRestartableRevisitAction(params: {
    payload: Record<string, unknown> | null | undefined;
    requestedCode: string;
    storedEntryCode: string;
    storedTestCode: string;
    hasActiveSession: boolean;
}): 'redirect-to-ctt-entry' | 'suppress-resume' | 'noop' {
    const { payload, requestedCode, storedEntryCode, storedTestCode, hasActiveSession } = params;
    if (hasActiveSession) return 'noop';
    if (!isTestEntryCode(requestedCode)) return 'noop';

    const cttId = Number(
        (payload?.testData as Record<string, unknown> | undefined)?.companyTestTypeId || 0
    );
    if (!cttId || readCttRestartableFromStartTest(payload, cttId)) return 'noop';
    if (!isStartTestInProgressForCtt(payload, cttId)) return 'noop';

    if (isCttEntryCode(storedEntryCode) && codesRepresentSameTakerTest(requestedCode, storedEntryCode, storedTestCode)) {
        return 'redirect-to-ctt-entry';
    }

    return 'suppress-resume';
}

export function stripStartTestResumeHints(
    payload: Record<string, unknown> | null | undefined
): Record<string, unknown> | null | undefined {
    if (!payload || typeof payload !== 'object') return payload;
    return {
        ...payload,
        lastSectionIdCompleted: 0,
        restartPage: null,
    };
}

export function shouldClearTakerTestIdentity(
    payload: Record<string, unknown> | null | undefined,
    storedCode: string,
    storedCttId: number
): boolean {
    const trimmedStoredCode = String(storedCode || '').trim();
    if (!trimmedStoredCode) return false;

    const testData =
        payload?.testData && typeof payload.testData === 'object'
            ? (payload.testData as Record<string, unknown>)
            : null;
    const payloadCode = String(testData?.code || payload?.code || '').trim();
    if (payloadCode && payloadCode !== trimmedStoredCode) return false;

    if (storedCttId > 0 && isStartTestResumableForCtt(payload, storedCttId)) {
        return false;
    }

    const blockingMessage = readBlockingStartTestMessage(payload);
    if (blockingMessage) return true;

    const stateId = Number(testData?.stateId || 0);
    return stateId >= 301;
}
